import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { isSupportedLocale } from '@/lib/locales/locales';
import { useRegionProvider } from '@/providers/RegionProvider';

import * as S from '../RetailerNavbar/RetailerNavbar.styles';

interface RetailerLanguageSwitcherProps {
  countryCode?: string;
  defaultLocale: string;
  locales: string[];
}

const getLanguageName = (locale: string): string => {
  const languageNames: Record<string, string> = {
    EN: 'English',
    ES: 'Español',
    FR: 'Français',
    DE: 'Deutsch',
    IT: 'Italiano',
    PT: 'Português',
    JA: '日本語',
    ZH: '中文',
    RU: 'Русский',
    ZHT: '中文（繁體）',
  };

  return languageNames[locale] || locale;
};

export const RetailerLanguageSwitcher: React.FC<
  RetailerLanguageSwitcherProps
> = ({ countryCode, defaultLocale, locales }) => {
  const { country: routeCountry } = useRegionProvider();
  const { locale: currentIntlLocale } = useIntl();
  const [currentLocale, setCurrentLocale] = useState(defaultLocale);
  const [isMounted, setIsMounted] = useState(false);

  const finalCountryCode = countryCode ?? routeCountry.code;
  const supportLocales = locales.filter((item) => {
    const formattedLocale = `${finalCountryCode.toLocaleLowerCase()}-${item.toLocaleLowerCase()}`;
    return isSupportedLocale(formattedLocale);
  });

  useEffect(() => {
    setIsMounted(true);

    if (typeof window !== 'undefined') {
      const pathSegments = window.location.pathname.split('/');
      const localePattern = /^[a-z]{2}-[a-z]{2,3}$/;
      const potentialLocale = pathSegments.find((segment) => {
        return localePattern.test(segment);
      });
      if (potentialLocale) {
        const langPart = potentialLocale.split('-')[1].toUpperCase();

        if (locales?.includes(langPart)) {
          setCurrentLocale(langPart);
          return;
        }
      }

      setCurrentLocale(defaultLocale);
    }
  }, [defaultLocale, locales, currentIntlLocale]);

  const handleLocaleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLocale = e.target.value;
    setCurrentLocale(newLocale);

    if (typeof window !== 'undefined') {
      const newLocaleCode = newLocale.toLowerCase();

      let formattedLocale = `${finalCountryCode?.toLocaleLowerCase()}-${newLocaleCode}`;

      if (!isSupportedLocale(formattedLocale)) {
        formattedLocale = `${finalCountryCode?.toLocaleLowerCase()}-en`;
      }

      const pathSegments = window.location.pathname.split('/');
      const localePattern = /^[a-z]{2}-[a-z]{2,3}$/;

      let localeFound = false;
      for (let i = 0; i < pathSegments.length; i++) {
        if (localePattern.test(pathSegments[i])) {
          pathSegments[i] = formattedLocale;
          localeFound = true;
          break;
        }
      }

      const newPath = localeFound
        ? pathSegments.join('/')
        : `/${formattedLocale}${window.location.pathname}`;

      const queryString = window.location.search;

      window.location.href = `${newPath}${queryString}`;
    }
  };

  if (!isMounted || supportLocales.length < 2) return null;

  return (
    <S.LanguageSwitcherContainer>
      <S.LanguageSelect onChange={handleLocaleChange} value={currentLocale}>
        {supportLocales.map((locale) => (
          <option key={locale} value={locale}>
            {getLanguageName(locale)}
          </option>
        ))}
      </S.LanguageSelect>
      <S.ArrowIcon>▼</S.ArrowIcon>
    </S.LanguageSwitcherContainer>
  );
};
